import React, { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import {
  CardSavingMode,
  CheckoutStateEnum,
  ICheckoutCart,
  IPaymentMethod,
  IPaymentTokenAccount,
  PaymentMethod,
  PaymentMethodsEnum,
  PaymentMethodsType,
  PaymentMethodTypes,
  PaymentMethodTypesEnum,
  PSPEnum,
} from '@monofront/types';
import {
  ABTEST_APPLE_PAY,
  FundingSources,
  PaypalTypesEnum,
} from '@monofront/business/constants';
import RadioLine from '@monofront/design-system/components/Form/RadioLine';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';

import { useAuthentication } from '@context/businessContainer';
import logger from '@utils/logger';
import OptionCheckoutComCard from './OptionCheckoutComCard';
import {
  CheckoutComFlowProvider,
  CheckoutComStoredCard,
} from '@components/PaymentForms/CheckoutComFlowForm';
import PaypalRow from '@components/PaypalRow';
import PaymentActions from '../PaymentActions';
import { useCheckoutPayment } from '../contexts';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import { useMemo } from 'react';
import { usePaypalContext } from '@context/PaypalProvider';
import InformativeBox from '@monofront/design-system/components/InformativeBox';
import CharacterSad from '@monofront/design-system/visuals/Illustrations/CharacterSad';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import useApplePayEligibility from '../hooks/useApplePayEligibility';
import CardGeneric from '@monofront/design-system/visuals/Icons/CardGeneric';
import Paypal from '@monofront/design-system/visuals/Icons/Paypal';
import ApplePay from '@monofront/design-system/visuals/Icons/ApplePay';
import InfoIcon from '@monofront/design-system/visuals/Icons/Info';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';

type PaymentOptionLabelProps = {
  text: string;
  image?: { src: string; width: number; height: number };
  icon?: React.ReactNode;
};

const PaymentOptionLabel: React.FC<PaymentOptionLabelProps> = ({
  text,
  image,
  icon,
}) => {
  return (
    <div className="tw-flex tw-items-center tw-gap-3">
      {icon}
      {image && (
        <ResponsiveImage
          alt=""
          src={image.src}
          height={image.height}
          width={image.width}
          aria-hidden
        />
      )}
      <span className="tw-lf-body-bold-md tw-font-bold">{text}</span>
    </div>
  );
};

type RenderProps = { isChecked: boolean };

type PaymentOptionConfig = {
  id: string;
  Label: (props: RenderProps) => JSX.Element;
  handleChecked: () => void;
  dataTestId: string;
  ExpandedContent?: (props: RenderProps) => JSX.Element;
  hideHeaderOnChecked?: boolean;
};

type PaymentOptionEntry = {
  getPaymentMethod: () => IPaymentMethod | undefined;
  build: (option: IPaymentMethod) => PaymentOptionConfig;
};

type PaymentOptionGroup = {
  configs: PaymentOptionConfig[];
  getContainerClassName: (args?: { isChildrenExpanded?: boolean }) => string;
};

type ExistingPaymentMethodEntry = {
  paymentMethod: PaymentMethod;
  paymentMethodType: PaymentMethodTypesEnum;
  paymentMethodCode: string;
};

type BuildPaymentOptionGroupsDeps = {
  usablePaymentOptions: IPaymentMethod[];
  existingPaymentMethods: ExistingPaymentMethodEntry[];
  isPaypalReady: boolean;
  isMethodEligible: (source: any) => boolean;
  isApplePayABTestEnabled: boolean;
  isApplePayAvailable: boolean;
  isSubscriptionAvailable: boolean;
  selectExistingPaymentMethod: (
    radioId: string,
    paymentMethod: {
      existingPaymentMethodId: string;
      paymentMethodCode: string;
      paymentMethodSource: PaymentMethod['source'];
    },
    paymentType: PaymentMethodTypesEnum,
  ) => void;
  selectNewCheckoutComOption: (
    radioId: string,
    paymentType: PaymentMethodTypesEnum,
  ) => void;
  selectNewPaypalOption: (
    radioId: string,
    paypalMethod: { paypalType: PaypalTypesEnum; paymentMethodCode: string },
    paymentType: PaymentMethodTypesEnum,
  ) => void;
  selectNewApplePayOption: (
    radioId: string,
    paymentType: PaymentMethodTypesEnum,
  ) => void;
  tCheckout: (key: any, ...args: any[]) => any;
};

const ExpandedContentWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="tw-px-6 tw-pb-6">{children}</div>;

const buildGroup = (entries: PaymentOptionEntry[]): PaymentOptionConfig[] =>
  entries
    .map(({ getPaymentMethod, build }) => {
      const option = getPaymentMethod();

      return option ? build(option) : null;
    })
    .filter((config): config is PaymentOptionConfig => config !== null);

const buildExistingPaymentOptionConfigs = (
  existingPaymentMethods: ExistingPaymentMethodEntry[],
  selectExistingPaymentMethod: BuildPaymentOptionGroupsDeps['selectExistingPaymentMethod'],
): PaymentOptionConfig[] =>
  existingPaymentMethods.map(
    ({ paymentMethod, paymentMethodType, paymentMethodCode }) => ({
      id: paymentMethod.id,
      Label: () => (
        <div className="tw-flex tw-items-center tw-gap-3">
          <PaypalRow paymentMethod={paymentMethod as IPaymentTokenAccount} />
        </div>
      ),
      handleChecked: () =>
        selectExistingPaymentMethod(
          paymentMethod.id,
          {
            existingPaymentMethodId: paymentMethod.id,
            paymentMethodCode,
            paymentMethodSource: paymentMethod.source,
          },
          paymentMethodType,
        ),
      dataTestId: `CheckoutPayment-RadioLine-existing-payment-${paymentMethod.id}`,
      ExpandedContent: () => (
        <ExpandedContentWrapper>
          <PaymentActions />
        </ExpandedContentWrapper>
      ),
    }),
  );

const buildPaymentOptionGroups = ({
  usablePaymentOptions,
  existingPaymentMethods,
  isPaypalReady,
  isMethodEligible,
  isApplePayABTestEnabled,
  isApplePayAvailable,
  isSubscriptionAvailable,
  selectExistingPaymentMethod,
  selectNewCheckoutComOption,
  selectNewPaypalOption,
  selectNewApplePayOption,
  tCheckout,
}: BuildPaymentOptionGroupsDeps): Record<string, PaymentOptionGroup> => {
  const cardEntry = {
    getPaymentMethod: () =>
      usablePaymentOptions.find(
        ({ method }) => PaymentMethodsEnum.CARD === method,
      ),
    build: ({ code, method }) => ({
      id: code,
      Label: () => (
        <PaymentOptionLabel
          text={tCheckout('payWithCard')}
          icon={<CardGeneric size={IconSizes.lg} />}
        />
      ),
      handleChecked: () => selectNewCheckoutComOption(code, method),
      dataTestId: `CheckoutPayment-RadioLine-new-card-payment-${code}`,
      ExpandedContent: () => <OptionCheckoutComCard />,
    }),
  };

  const otherEntries = [
    {
      getPaymentMethod: () =>
        usablePaymentOptions.find(({ psp }) => PSPEnum.PAYPAL === psp),
      build: ({ code, method }) => ({
        id: code,
        Label: () => (
          <PaymentOptionLabel
            text={tCheckout('payWithPaypal')}
            icon={<Paypal size={IconSizes.lg} />}
          />
        ),
        handleChecked: () =>
          selectNewPaypalOption(
            code,
            {
              paypalType: isSubscriptionAvailable
                ? PaypalTypesEnum.PAYPAL_AGREEMENT
                : PaypalTypesEnum.PAYPAL,
              paymentMethodCode: code,
            },
            method,
          ),
        dataTestId: `CheckoutPayment-RadioLine-new-paypal-payment-${code}`,
        ExpandedContent: () => (
          <ExpandedContentWrapper>
            <p className="tw-mb-4">
              <InfoIcon
                UNSAFE_className="tw-mr-1 tw-mb-[-2px]"
                size={IconSizes.sm}
              />
              {tCheckout('flowPaypalInfos')}
            </p>
            <PaymentActions />
          </ExpandedContentWrapper>
        ),
      }),
    },
    // note: the same Sylius payment option is used for both Paypal and Paypal4x
    {
      getPaymentMethod: () =>
        isPaypalReady && isMethodEligible(FundingSources.PAY_LATER)
          ? usablePaymentOptions.find(({ psp }) => PSPEnum.PAYPAL === psp)
          : undefined,
      build: ({ code }) => ({
        id: `${code}-4x`,
        Label: () => (
          <PaymentOptionLabel
            text={tCheckout('payWithPaypal4x')}
            icon={<Paypal size={IconSizes.lg} />}
          />
        ),
        handleChecked: () =>
          selectNewPaypalOption(
            `${code}-4x`,
            {
              paypalType: PaypalTypesEnum.PAYPAL4X,
              paymentMethodCode: code,
            },
            PaymentMethodTypes.PAYPAL4X,
          ),
        dataTestId: `CheckoutPayment-RadioLine-new-paypal-payment-${code}-4x`,
        ExpandedContent: () => (
          <ExpandedContentWrapper>
            <p className="tw-mb-4">
              <InfoIcon size={IconSizes.sm} />
              {tCheckout('flowPaypalInfos')}
            </p>
            <PaymentActions />
          </ExpandedContentWrapper>
        ),
      }),
    },
    {
      getPaymentMethod: () =>
        isApplePayABTestEnabled && isApplePayAvailable
          ? usablePaymentOptions.find(
              ({ method }) => PaymentMethodsEnum.APPLE_PAY === method,
            )
          : undefined,
      build: ({ code, method }) => ({
        id: code,
        Label: () => (
          <PaymentOptionLabel
            text={tCheckout('payWithApplePay')}
            icon={<ApplePay size={IconSizes.lg} />}
          />
        ),
        handleChecked: () => selectNewApplePayOption(code, method),
        dataTestId: `CheckoutPayment-RadioLine-new-card-payment-${code}`,
        ExpandedContent: () => (
          <ExpandedContentWrapper>
            <PaymentActions />
          </ExpandedContentWrapper>
        ),
      }),
    },
  ];

  return {
    existing: {
      configs: buildExistingPaymentOptionConfigs(
        existingPaymentMethods,
        selectExistingPaymentMethod,
      ),
      getContainerClassName: () =>
        'tw-border tw-border-[#dfe4f3] tw-border-px tw-border-solid tw-mt-[-1px]',
    },
    card: {
      configs: buildGroup([cardEntry]),
      // -1px top margin collapses the shared edge with the row above so
      // adjacent borders read as a single line instead of a double border.
      getContainerClassName: () =>
        'tw-border tw-border-[#dfe4f3] tw-border-px tw-border-solid tw-mt-[-1px]',
    },
    other: {
      configs: buildGroup(otherEntries),
      getContainerClassName: () =>
        'tw-border tw-border-[#dfe4f3] tw-border-px tw-border-solid tw-mt-[-1px]',
    },
  };
};

type PaymentMethodsListProps = {
  checkoutCart: ICheckoutCart;
};

const PaymentMethodsList: React.FC<PaymentMethodsListProps> = ({
  checkoutCart,
}) => {
  const tCheckout = useTranslations('Checkout');
  const tCheckoutPayment = useTranslations('Checkout.Payment');
  const { authUser } = useAuthentication();
  const { isReady: isPaypalReady, isMethodEligible } = usePaypalContext();
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);
  const {
    isOrderValidationInProgress,
    isFlowInitializing,
    selectedPaymentRadioId,
    newPaymentOptions,
    savedPaymentMethods,
    selectExistingPaymentMethod,
    selectNewCheckoutComOption,
    selectNewPaypalOption,
    selectNewApplePayOption,
    markFlowInitializing,
    markFlowReady,
    clearSelectedPaymentMethod,
  } = useCheckoutPayment();
  const cardSaving = isSubscriptionAvailable
    ? CardSavingMode.FORCE_SAVE
    : CardSavingMode.SAVE_IF_CONSENTED;
  const isApplePayABTestEnabled = useFeatureIsOn(ABTEST_APPLE_PAY);
  const { isApplePayAvailable } = useApplePayEligibility();
  const optionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const hasBeenReadyRef = useRef(false);

  useEffect(() => {
    if (!selectedPaymentRadioId) return;
    optionRefs.current
      .get(selectedPaymentRadioId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedPaymentRadioId]);

  // Show the initializing loader while the Checkout.com widget loads; cleared
  // by handleFlowReady (the widget's onReady). Only before the first ready: a
  // later LIST_PAYMENT_METHODS refetch (e.g. checkoutCart.payment.id changes)
  // can change newPaymentOptions.length again, but the widget isn't remounted
  // so onReady won't fire a second time to clear it.
  useEffect(() => {
    if (newPaymentOptions.length && !hasBeenReadyRef.current) {
      markFlowInitializing();
    }
  }, [newPaymentOptions.length]);

  // Default selection: prefer the member's saved card (owned by the widget, so
  // the React selection stays idle). When no saved card is available, preselect
  // the new-card option so its form is ready.
  const handleFlowReady = (hasStoredCard: boolean) => {
    hasBeenReadyRef.current = true;
    markFlowReady();

    if (hasStoredCard || selectedPaymentRadioId !== null) return;

    const cardOption = newPaymentOptions.find(
      ({ method }) => method === PaymentMethodsEnum.CARD,
    );

    if (cardOption) {
      selectNewCheckoutComOption(
        cardOption.code,
        cardOption.method as PaymentMethodTypesEnum,
      );
    }
  };

  const usablePaymentOptions = useMemo(() => {
    return newPaymentOptions.filter((paymentOption) => {
      if (!paymentOption.publishableKey) {
        const warnMessage = {
          message:
            '[checkout] Cannot display a user Payment Option without publishableKey.',
          context: {
            user_id: authUser?.uid,
            cart_id: checkoutCart?.id,
            payment_option_id: paymentOption.id,
            payment_option_code: paymentOption.code,
          },
        };

        logger.warn(warnMessage.message, warnMessage.context);

        return false;
      }

      return true;
    });
  }, [newPaymentOptions.length]);

  // Saved cards are already rendered by the CKO Flow `stored_card` widget
  // (see CheckoutComStoredCard below); only non-card saved methods (e.g. a
  // saved PayPal account) need their own radio option here.
  const existingPaymentMethods = useMemo<ExistingPaymentMethodEntry[]>(() => {
    return savedPaymentMethods.reduce((entries, paymentMethod) => {
      if (paymentMethod.paymentType === PaymentMethodsType.Card) {
        return entries;
      }

      const associatedPaymentOption = newPaymentOptions.find(
        (pm) => (pm.type as string) === paymentMethod.source,
      );

      if (!associatedPaymentOption) {
        logger.warn(
          '[checkout] Cannot display a user Payment Method with no associated OMS payment option.',
          {
            user_id: authUser?.uid,
            cart_id: checkoutCart?.id,
            pm_id: paymentMethod.id,
          },
        );

        return entries;
      }

      return [
        ...entries,
        {
          paymentMethod,
          paymentMethodType: associatedPaymentOption.method,
          paymentMethodCode: associatedPaymentOption.code,
        },
      ];
    }, [] as ExistingPaymentMethodEntry[]);
  }, [savedPaymentMethods.length, newPaymentOptions.length]);

  const paymentOptionGroups = useMemo<Record<string, PaymentOptionGroup>>(
    () =>
      buildPaymentOptionGroups({
        usablePaymentOptions,
        existingPaymentMethods,
        isPaypalReady,
        isMethodEligible,
        isApplePayABTestEnabled,
        isApplePayAvailable,
        isSubscriptionAvailable,
        selectExistingPaymentMethod,
        selectNewCheckoutComOption,
        selectNewPaypalOption,
        selectNewApplePayOption,
        tCheckout,
      }),
    [
      usablePaymentOptions,
      existingPaymentMethods,
      isPaypalReady,
      isApplePayABTestEnabled,
      isApplePayAvailable,
      savedPaymentMethods.length,
      isSubscriptionAvailable,
    ],
  );

  if (!usablePaymentOptions.length) {
    return CheckoutStateEnum.PAYMENT_SKIPPED ===
      checkoutCart.checkoutState ? null : (
      <InformativeBox
        backgroundColor={'var(--clr-secondary-3)'}
        hasImageOnMobile
        icon={<CharacterSad width={34} height={34} aria-hidden="true" />}
        smallBox={true}
        UNSAFE_className={'tw-mb-5'}
      >
        <p className="tw-lf-body-regular-sm tw-leading-normal">
          {tCheckoutPayment.rich('messageCannotFetchNewPM', {
            strong: (children) => <strong>{children}</strong>,
            br: () => <br />,
          })}
        </p>
      </InformativeBox>
    );
  }

  return (
    <>
      {isFlowInitializing && <HeartLoader />}
      <section
        className={`tw-w-full desktop:tw-max-w-[520px]${
          isFlowInitializing ? ' tw-hidden' : ''
        } desktop:tw-mb-8`}
      >
        <h2 className="tw-text-md tw-lf-body-xbold-md tw-mb-2">
          {tCheckout('paymentMethods')}
        </h2>

        <CheckoutComFlowProvider
          tokenValue={checkoutCart.id}
          cartTotal={checkoutCart.total}
          cardSaving={cardSaving}
          onReady={handleFlowReady}
          onStoredCardSelected={clearSelectedPaymentMethod}
        >
          <CheckoutComStoredCard
            selectedPaymentRadioId={selectedPaymentRadioId}
          />

          {Object.entries(paymentOptionGroups)
            .filter(([, { configs }]) => configs.length > 0)
            .map(([key, { configs, getContainerClassName }]) => {
              const containerClassName = getContainerClassName({
                isChildrenExpanded: configs.some(
                  ({ id }) => selectedPaymentRadioId === id,
                ),
              });

              return (
                <div key={key} className={`${containerClassName}`}>
                  {configs.map(
                    (
                      {
                        id,
                        Label,
                        handleChecked,
                        dataTestId,
                        ExpandedContent,
                        hideHeaderOnChecked,
                      },
                      index,
                    ) => {
                      const isChecked = selectedPaymentRadioId === id;
                      const isLastElement = configs.length === index + 1;

                      return (
                        <div
                          key={id}
                          ref={(el) => {
                            if (el) optionRefs.current.set(id, el);
                            else optionRefs.current.delete(id);
                          }}
                          style={{
                            scrollMarginTop: 'var(--checkout-header-height)',
                          }}
                        >
                          {(!isChecked || !hideHeaderOnChecked) && (
                            <li className="tw-list-none tw-p-3">
                              <RadioLine
                                id={id}
                                name="checkout-available-payment-list"
                                value={id}
                                checked={isChecked}
                                handleChecked={handleChecked}
                                required
                                disabled={isOrderValidationInProgress}
                                isTextBoldWhenChecked
                                isVerticallyCentered
                                isBackgroundColoredWhenChecked={false}
                                dataTestId={dataTestId}
                              >
                                <Label isChecked={isChecked} />
                              </RadioLine>
                            </li>
                          )}
                          <div
                            className={
                              isChecked && !!ExpandedContent ? '' : 'tw-hidden'
                            }
                          >
                            {ExpandedContent && (
                              <ExpandedContent isChecked={isChecked} />
                            )}
                          </div>
                          {!isLastElement && (
                            <div className="tw-h-px tw-w-full tw-bg-blue-200" />
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              );
            })}
        </CheckoutComFlowProvider>
      </section>
    </>
  );
};

export default PaymentMethodsList;
